/**
 * Brand marks for the login gate.
 *
 * Isolated in one file on purpose: the Silverback lockup is the company's real
 * artwork, ported from the design system rather than redrawn, so a brand update
 * lands here and nowhere else.
 *
 * Inline SVG rather than `<img>`: these are two small shapes on the one page
 * that must render before anything is trusted, so an inline mark cannot fail to
 * load, needs no extra request, and follows `currentColor` instead of being
 * baked to one colour.
 *
 * ── Why masks, and not a second fill colour ──────────────────────────────
 * Features are punched out with a `<mask>` so they are genuine holes showing
 * whatever is behind. The first attempt filled them with `var(--s1)`, which
 * looked like a background colour and is not: the surface tokens in tokens.css
 * are TRANSLUCENT (`--s1` is `rgba(255,255,255,0.03)`). Painting 3% white over
 * a solid mark does nothing, so every eye and muzzle was invisible. A mask has
 * no colour to get wrong and survives any future theme.
 *
 * Shapes are kept coarse deliberately. These render at 26–30px, where a 1px
 * detail is noise rather than definition.
 */

/**
 * Silverback AI — the real wordmark, ported from
 * ~/workspace/silverbackos-publish/grodd/design-system/dls-jun4/brand.css.
 *
 * Two things about it are easy to get wrong, and my first attempt got both:
 *
 *  1. **The mark is a bell curve, not an animal.** It stands in for the "A" of
 *     "AI" — gold outer curve, silver inner crossbar. There is no gorilla
 *     anywhere in this brand.
 *  2. **There is no separate icon.** The source calls the wordmark
 *     "self-contained: Silverback + [crest-a glyph] + I … No floating icon to
 *     the left." Placing a standalone crest above the wordmark would print the
 *     same mark twice.
 *
 * The "i" is dotless (ı) with the tittle drawn as a gold disc, which is why the
 * text cannot simply be typed.
 *
 * Class names are the canonical `bell` / `bell-inner` / `bell-outer`, matching
 * what app.silverbackos.ai/signin actually renders. brand.css keeps
 * `crest-a-*` as aliases but says new markup should not use them.
 *
 * Fraunces is NOT loaded here — grizzly ships Inter and JetBrains Mono, and the
 * CSP allows `font-src 'self' data:` only, so a CDN link would silently fail.
 * The stack below is the brand's own documented fallback, so this degrades the
 * way its authors intended rather than the way I chose.
 */
export const SILVERBACK_WORDMARK = `<span class="sbai" role="img" aria-label="Silverback AI">S<span
        class="sbai-i">&#305;<i class="sbai-tittle"></i></span>lverback<span class="sbai-AI"><svg
          class="bell" viewBox="0 0 80 104" fill="none" aria-hidden="true" focusable="false">
          <path class="bell-inner" d="M 9 100 C 21 100, 28 54, 40 54 C 52 54, 59 100, 71 100"
            stroke-width="4.2" stroke-linecap="round" />
          <path class="bell-outer" d="M 6 100 C 19 100, 27 8, 40 8 C 53 8, 61 100, 74 100"
            stroke-width="7.6" stroke-linecap="round" />
        </svg><span class="sbai-I">I</span></span></span>`

/** Grizzly — a bear's head, front on. Tall round ears are the whole silhouette. */
export const GRIZZLY_BEAR_MARK = `<svg class="login-bear" viewBox="0 0 32 32" aria-hidden="true">
      <mask id="bear-mark-mask">
        <rect width="32" height="32" fill="black" />
        <circle cx="7.4" cy="8" r="4.5" fill="white" />
        <circle cx="24.6" cy="8" r="4.5" fill="white" />
        <path
          d="M16 5.8c5.9 0 10.1 4.2 10.1 10.1 0 6.3-4.4 10.7-10.1 10.7S5.9 22.2 5.9 15.9C5.9 10 10.1 5.8 16 5.8Z"
          fill="white"
        />
        <ellipse cx="16" cy="20.8" rx="5.1" ry="3.9" fill="black" />
        <circle cx="11.5" cy="14" r="1.75" fill="black" />
        <circle cx="20.5" cy="14" r="1.75" fill="black" />
      </mask>
      <rect width="32" height="32" fill="currentColor" mask="url(#bear-mark-mask)" />
      <ellipse cx="16" cy="19.4" rx="2.2" ry="1.7" fill="currentColor" />
    </svg>`
